import { DateTime } from "luxon";
import { formatDate } from "swing-components";

const string_table = {
  SUPPORT_EMAIL: () => "support@swingeducation.com",
  SUPPORT_PHONE: () => "(650)413-9268",
  SUB_SUMMARY_LABEL_HEADER: () => "Sub Info",
  SUB_SUMMARY_LABEL_FILLED_BY: () => "Filled by",
  SUB_SUMMARY_LABEL_CANCEL_SUMMARY: () =>
    "This request was cancelled and subs will not be able to sign up.",
  SUB_SUMMARY_LABEL_STILL_OPEN: () => "This request is still open to subs.",
  SUB_SUMMARY_BUTTON_REPORT_AN_ISSUE: () => "Report an Issue",
  CANCEL_REQ_LABEL_HEADER: () => "Cancel Request",
  CANCEL_REQ_LABEL_WHY_CANCEL: () => "Why are you cancelling this request?",
  CANCEL_REQ_LABEL_CONFIRM_CANCEL: () => "Cancel this request?",
  CANCEL_REQ_LABEL_WHY_DISSATISFIED: () => "Why are you dissatisfied?",
  CANCEL_REQ_LABEL_WHAT_OTHER_REASON: () => "What is the reason for cancellation?",
  CANCEL_REQ_LABEL_ADD_FEEDBACK: () => "Please add some feedback.",
  CANCEL_REQ_LABEL_ADD_EXPLANATION: () => "Please briefly explain.",
  CANCEL_REQ_LABEL_REASON_SELECTED: () => "Reason selected: ",
  CANCEL_REQ_LABEL_CANCELLATION_REASON: () => "Cancellation reason: ",
  CANCEL_REQ_LABEL_CANCELLATION_EXPLANATION: () => "Your notes: ",
  CANCEL_REQ_LABEL_APOLOGY: () =>
    "We're sorry to hear you're not happy with this sub. Let us know about what happened at your school site.",
  CANCEL_REQ_LABEL_CANCELLATION_POLICY: () => "cancellation policy",
  CANCEL_REQ_LABEL_GENERAL_WARNING: () =>
    "Subs will not be able to sign up for this request after canceling.",
  CANCEL_REQ_LABEL_NO_CHARGE: () =>
    "You will not be charged a cancellation fee. The sub will be blocked from future requests.",
  CANCEL_REQ_LABEL_REPOST_REQUEST: () =>
    "Would you like us to repost the remaining days and find a new sub?",
  CANCEL_REQ_LABEL_REPOST_REQUEST_YES: () => "Yes, find a replacement",
  CANCEL_REQ_LABEL_REPOST_REQUEST_NO: () => "No, I no longer need a sub",
  CANCEL_REQ_LABEL_REPOST_ALERT_MESSAGE: () =>
    "We're searching for one of our top subs to fill this request. Your original request has been canceled.",
  CANCEL_REQ_LABEL_LAST_MIN_WARNING: () =>
    "Cancelling now is considered last minute and a sub is assigned to this request. If you cancel, you will still be invoiced based on your",
  CANCEL_REQ_LABEL_HEADER_REPOSTED: () => "We've reposted your request",
  CANCEL_REQ_LABEL_HEADER_CANCELLED: () => "Cancellation complete",
  CANCEL_REQ_LABEL_SWING_TO_INVESTIGATE: () =>
    "Our team will investigate this incident and may be in touch.",
  CANCEL_REQ_LABEL_REPOSTED_MESSAGE: () =>
    "Your new request has been created and we're notifying our top subs.",
  CANCEL_REQ_LABEL_CANCELLED_ORIGINAL_REQUEST: () =>
    "Original request canceled. You will not be charged for today.",
  CANCEL_REQ_LABEL_CANCELLED_MESSAGE: () =>
    "Your request was successfully canceled. Subs will no longer be able to sign up.",
  CANCEL_REQ_LABEL_SUB_BLOCKED: ({ subName }: { subName: string }) =>
    `${subName} is no longer able to accept requests at your school.`,
  CANCEL_REQ_REASON_SUBMIT_ERROR: () => "Submitted in error",
  CANCEL_REQ_REASON_FILLED_BY_ALT_SRC: () => "Filled by another source",
  CANCEL_REQ_REASON_SUB_NOT_NEEDED: () => "Sub no longer needed",
  CANCEL_REQ_REASON_DISSATISFIED: () => "Dissatisfied with sub",
  CANCEL_REQ_REASON_OTHER: () => "Other",
  CANCEL_REQ_REASON_EDIT_REQUEST: () => "Need to edit request info",
  CANCEL_REQ_REASON_NOT_FILLED_IN_TIME: () => "Wasn't filled in time",
  CANCEL_REQ_REASON_PREFER_DIFFERENT_SUB: () => "Prefer different sub",
  CANCEL_REQ_REASON_UNHAPPY_WITH_WAITLIST: () => "Unhappy with waitlist",
  CANCEL_REQ_BUTTON_VIEW_REQUEST: () => "View New Request",
  CANCEL_REQ_BUTTON_RETURN_TO_REQUESTS: () => "Return to Requests",
  CANCEL_REQ_BUTTON_PROCESSING: () => "Processing",
  CANCEL_REQ_BUTTON_DO_NOT_CANCEL: () => "Don't Cancel",
  CANCEL_REQ_BUTTON_CONFIRM_CANCEL: () => "Yes, Cancel",
  CANCEL_REQ_SUB_NO_LONGER_NEEDED_WHY: () => "Why is your sub no longer needed?",
  CANCEL_REQ_BUTTON_CONFIRM_DO_NOT_CANCEL: () => "No, Don't Cancel",
  CANCEL_REQ_SUB_NO_LONGER_NEEDED_AGENCY: () => "Filled by another agency",
  CANCEL_REQ_SUB_NO_LONGER_NEEDED_INTERNAL: () => "Got internal coverage",
  CANCEL_REQ_SUB_NO_LONGER_NEEDED_PERMANENT: () => "Hired permanent staff",
  CANCEL_REQ_SUB_NO_LONGER_NEEDED_GENERIC: () => "No longer need coverage for role",
  /** LABELS **/
  LABEL_ACTIVE_REQUESTS: () => "Active Requests",
  LABEL_ACTIVITY_LOG: () => "Activity Log",
  LABEL_ACTIVE_REQUESTS_COUNT: ({ count }: { count: number }) =>
    `${count} active request${count === 1 ? "" : "s"}`,
  LABEL_PAST_REQUESTS_COUNT: ({ count }: { count: number }) =>
    `${count} past request${count === 1 ? "" : "s"}`,
  LABEL_AUTO_ADJUSTED_START_DATE: () => "Auto-adjusted start date",
  LABEL_PAST_REQUESTS: () => "Past Requests",
  LABEL_BACK: () => "Back",
  LABEL_NEXT: () => "Next",
  LABEL_CANCELLED: () => "Cancelled",
  LABEL_CONFIRM_BUDGET: () => "Confirm Budget",
  LABEL_FILLED: () => "Filled",
  LABEL_FILLED_BY: () => "Filled by",
  LABEL_UNFILLED: () => "Unfilled",
  LABEL_COMPLETED: () => "Completed",
  LABEL_INTERNAL_ERROR: ({ errorCode }: { errorCode: string }) => `An error occurred. ${errorCode}`,
  LABEL_CLOSE: () => "Close",
  LABEL_CANCEL: () => "Cancel",
  LABEL_CONFIRM: () => "Confirm",
  LABEL_SUBMIT: () => "Submit",
  LABEL_YES: () => "Yes",
  LABEL_NO: () => "No",
  LABEL_OR: () => "OR",
  LABEL_NEVERMIND: () => "Nevermind",
  LABEL_DONE: () => "Done",
  LABEL_DATE: () => "Date",
  LABEL_START: () => "Start",
  LABEL_START_DATE: () => "Start Date",
  LABEL_END: () => "End",
  LABEL_END_DATE: () => "End Date",
  LABEL_EDIT: () => "Edit",
  LABEL_NONE: () => "None",
  LABEL_EDIT_START_DATE: () => "Edit Start Date",
  LABEL_EDIT_END_DATE: () => "Edit End Date",
  LABEL_REQUIRED: () => " (required)",
  LABEL_REQUEST_SUB: () => "Submit Request",
  LABEL_DETAILS: () => "Details",
  LABEL_SCHOOLS: () => "Schools",
  LABEL_STATUS: () => "Status",
  LABEL_TEACHER: () => "Teacher",
  LABEL_CONTACT: () => "Contact",
  LABEL_CONTACT_NUMBER: () => "Contact Number",
  LABEL_DAY_OF_CONTACT: () => "Day of Contact",
  LABEL_GRADE: () => "Grade",
  LABEL_SUBJECT: () => "Subject",
  LABEL_BILINGUAL_REQUIREMENT: () => "Bilingual Requirement",
  BILINGUAL_REQUIREMENT_DESCRIPTION: () =>
    "Which languages besides English does your sub need to speak?",
  LABEL_OTHER_LANGUAGE: () => "Specify Other Language",
  OTHER_LANGUAGE_ERROR: () => "Please specify the other language",
  NO_LANGUAGE_REQUIREMENT: () => "No language requirement",
  LABEL_LESSON_PLAN: () => "Lesson Plan",
  LABEL_CRED_PERMIT: () => "Cred/Permit",
  LABEL_INTERNAL_CODE: () => "Internal Code",
  LABEL_NOTES: () => "Notes",
  LABEL_MULTI_DAY: () => "Multi-day",
  LABEL_POSITION: () => "Position",
  LABEL_VIEW: () => "View",
  LABEL_START_TIME: () => "Start Time",
  LABEL_SELECT_ALL_THAT_APPLY: () => "Select all that apply.",
  LABEL_APPLY: () => "Apply",
  LABEL_END_TIME: () => "End Time",
  LABEL_AM: () => "AM",
  LABEL_PM: () => "PM",
  LABEL_PENDING_ADJUSTMENTS: () => "Pending adjustments",
  LABEL_LONG_TERM_ADDITIONAL_DETAILS: () => "We need additional details",
  LABEL_POSITION_FIELD: () => "Position Requested",
  POSITION_RATE_GROUP_DAILY: () => "Daily",
  POSITION_RATE_GROUP_HOURLY: () => "Hourly",
  POSITION_RATE_GROUP_OTHERS: () => "Others",
  POSITION_CREDENTIAL_REQUIRED_TAG: () => "Credential or Permit Required",
  POSITION_CUSTOM_POSITION_MESSAGE: () =>
    "Want to add a custom position? Reach out to your customer success manager or support.",
  LABEL_BUDGET: () => "Budget",
  LABEL_SOMETHING_WENT_WRONG: () => "Something went wrong",
  LABEL_CONTACT_SUPPORT_1: () => "Please try again later or",
  LABEL_CONTACT_SUPPORT: () => "contact support",
  LABEL_CONTACT_SUPPORT_2: () => "for assistance.",
  LABEL_REQUEST_DETAILS_NAV_TITLE: () => "Request Details",
  LABEL_ACTIVE_UNFILLED_REQUEST_INDEX_TAB: () => "Unfilled",
  LABEL_ACTIVE_WAITLIST_REQUEST_INDEX_TAB: () => "Waitlist",
  LABEL_ACTIVE_FILLED_REQUEST_INDEX_TAB: () => "Filled",
  LABEL_PAST_COMPLETED_REQUEST_INDEX_TAB: () => "Completed",
  LABEL_PAST_CANCELED_REQUEST_INDEX_TAB: () => "Canceled",
  LABEL_PAST_UNFILLED_REQUEST_INDEX_TAB: () => "Unfilled",
  LABEL_EDIT_REQUEST_SUCCESS: () => "Edits successfully updated:",
  LABEL_MULTIPLE: () => "Multiple",
  SELECT_PLACEHOLDER: () => "Select",
  TIME_TO_ACCEPT_LABEL: () => "Open to all eligible subs after",
  TIME_TO_ACCEPT_ONLY_SELECTED_SUBS_VALUE: () => "Only selected subs can sign up",
  TIME_TO_ACCEPT_VALUE: ({ hours }: { hours: number }) => {
    // If less than 1 hour, display in minutes
    if (hours < 1) {
      const minutes = hours * 60;
      return minutes === 1 ? "1 minute" : `${minutes} minutes`;
    }
    return hours === 1 ? "1 hour" : `${hours} hours`;
  },
  TIME_TO_ACCEPT_MODAL_HEADER: () => "Open to all eligible subs after",
  TIME_TO_ACCEPT_MODAL_DESCRIPTION: () => "How long before opening to all eligible subs?",
  TIME_TO_ACCEPT_TOOLTIP_HEADER: () => "Time for subs to accept",
  TIME_TO_ACCEPT_TOOLTIP_CONTENT: () =>
    "Your specifically requested subs will have this amount of time to accept the request before we notify the rest of your sub pool.",
  SUB_SELECT_TITLE: () => "Priority Access",
  SUB_SELECT_HEADER: () => "Choose Sub(s)",
  SUB_SELECT_DISPLAY_VALUE: ({ count }: { count: number }) => {
    if (count === 0) return "";
    return count === 1 ? "1 sub selected" : `${count} subs selected`;
  },
  SUB_SELECT_ALERT_CREDENTIALED: () => "Only credentialed subs allowed due to selected position.",
  SUB_SELECT_LABEL_UNAVAILABLE: () => "unavailable",
  SUB_SELECT_LABEL_NON_CRED: () => "non-cred",
  SUB_SELECT_LABEL_ALLOW_CRED_ONLY: () => "Only allow subs with credential or permit",
  SUB_SELECT_LABEL_ALL_SECTION: () => "All Subs",
  SUB_SELECT_LABEL_INTERNAL_SECTION: () => "Internal Subs",
  SUB_SELECT_LABEL_FAVORITES_SECTION: () => "My Favorites",
  SUB_SELECT_REQUIRED_ERROR: () => "Please select at least 1 sub",
  SUB_PROFILE_CONTACT_SWING: () => "Swing",
  SUB_PROFILE_CONTACT_HERO: () => "Hero",
  GRADE_MODAL_HEADER_LABEL: () => "Choose Grade(s)",
  GRADE_MODAL_SELECT_BY: () => "Select by group:",
  GRADE_MODAL_BUTTON_1: () => "Pre-K to 5",
  GRADE_MODAL_BUTTON_2: () => "6 to 8",
  GRADE_MODAL_BUTTON_3: () => "9 to 12",
  GRADE_MODAL_SELECT_ALL: () => "Select All",
  GRADE_MODAL_CLEAR_ALL: () => "Clear All",
  SELECT_ALL: () => "Select All",
  CLEAR_ALL: () => "Clear All",
  CLEAR_ALL_SUBS: () => "Clear All Subs",
  SUBJECT_MODAL_HEADER_LABEL: () => "Select Subject",
  BILINGUAL_REQUIREMENT_MODAL_HEADER: () => "Bilingual Requirement",
  TEACHER_MODAL_HEADER: () => "Select Staff",
  TEACHER_LABEL_VACANCY: () => "Staff name or identifier",
  TEACHER_LABEL_ROVING: () => "Floater/Roving Sub",
  TEACHER_INPUT_PLACEHOLDER: () => "Optional: Type staff name or identifier",
  NOTES_ROVING_MESSAGE: () =>
    "You will receive more details about your teaching assignment when you check in at the school. You may be in one classroom or moving to different classrooms throughout the day.",
  SCHOOL_LABEL: () => "School",
  SCHOOL_MODAL_HEADER: () => "Select a School",
  TIMEZONE_DISCLAIMER_LABEL: ({
    schoolName,
    timeZone,
  }: {
    schoolName: string;
    timeZone?: string;
  }) => `Times are displayed in ${schoolName}'s timezone (${timeZone})`,
  EDIT_REQUEST_HEADER: () => "Edit Request",
  EDIT_REQUEST_SUBMIT_LABEL: () => "Submit Edit",
  EDIT_REQUEST_ACCEPTED_CANCEL_LABEL: () => "I don't want to edit",
  EDIT_REQUEST_FILLED_HEADER: () => "Extend your request in seconds",
  EDIT_REQUEST_FILLED_MESSAGE: () =>
    "Simply change the end date below to keep your sub for longer.",
  EDIT_REQUEST_ADDITIONAL_EDITS_LABEL: () => "I need additional edits",
  EDIT_REQUEST_ADDITIONAL_EDITS_HEADER: () => "Any additional edits you'd like to make?",
  EDIT_REQUEST_ADDITIONAL_EDITS_MESSAGE: () =>
    "We'll do our best to accommodate additional edits below when possible. Please enter your edits below.",
  EDIT_REQUEST_ADDITIONAL_EDITS_PLACEHOLDER: () => "e.g. Remove every other Thursday",
  BUDGET_FIELD_REQUIRED_ERROR: () => "Please select a budget",
  REQ_START_TIME_ERROR: () => "Start time must be at least 1 hr from now.",
  REQ_END_TIME_ERROR: () => "End time must be at least 1 hr from start.",
  GENERAL_ERROR: () => "Sorry, we've encountered an error",
  INTERNAL_CODE_ERROR: () => "Your school requires an internal code.",
  SUBMIT_REQUEST_ERROR_HEADER: () => "Could not submit request",
  SUBMIT_REQUEST_ERROR_MESSAGE: () => "There were missing or invalid fields in your submission",
  CANCELLATION_WINDOW_LABEL: () => "Within cancellation window",
  CANCELLATION_WINDOW_TOOLTIP_MESSAGE: () =>
    "You are submitting a request within your cancellation window. If a Swing Sub accepts this request, you will have 30 minutes to cancel it before you will be invoiced.",
  MULTI_DAY_ALL_DATES_TIMES_LABEL: () => "All Dates/Times",
  MULTI_DAY_EDIT_TIMES_LABEL: () => "Edit Times",
  MULTI_DAY_ADD_DATE_LABEL: () => "+ Add another day",
  DASH_TEXT: () => "-",
  WAITLIST: () => "Waitlist",
  LONG_TERM_ADDITIONAL_EDITS_ERROR_MESSAGE: () =>
    'Please list days that need to be adjusted or write "N/A"',
  LONG_TERM_ADDITIONAL_DETAILS_EDIT_TOOLTIP_1: ({ contactLink }: { contactLink: string }) =>
    `After submitting initial request, ${contactLink} to make changes to this section.`,
  WAITLIST_SUB_COUNT_LABEL_MULTIPLE: () => "subs on waitlist",
  WAITLIST_SUB_COUNT_LABEL_SINGLE: () => "sub on waitlist",
  WAITLIST_INSTANT_FILL_LABEL: () => "Instant Fill",
  WAITLIST_N_DAYS_WORKED: () => "District days worked",
  WAITLIST_NO_DAYS_WORKED: () => "No district days yet",
  WAITLIST_MORE_SUB: () => "more sub on waitlist",
  WAITLIST_MORE_SUBS: () => "more subs on waitlist",
  CHOOSE_SUB_LABEL: () => "Choose Sub",
  CONFIRM_SUB_HEADER: () => "Confirm Sub",
  PLEASE_CONFIRM_LABEL: () => "Please confirm",
  SELECTING_SUB_LABEL_1: () => "You're selecting",
  SELECTING_SUB_LABEL_2: () => "for this request",
  COMMITMENTS_LABEL: (subName: string) =>
    `Please give ${subName} 12 hours to respond to this request since ${subName} will have to cancel conflicting commitments`,
  COMMITMENTS_EMAIL_LABEL: () => "We will email you within 12 hours with the sub's response.",
  LESSON_PLAN_ON_ARRIVAL: () => "Provided upon arrival",
  NOT_PROVIDED: () => "Not provided",
  NOT_REQUIRED: () => "Not Required",
  UNKNOWN: () => "Unknown",
  UNKNOWN_SEE_NOTES: () => "Unknown (See Notes)",
  CREDENTIALS_REQUIRED: () => "Required",
  CREDENTIALS_NOT_REQUIRED: () => "Not required",
  LABEL_WAITING: () => "Waiting",
  LABEL_WAITING_FOR_1: () => "Waiting for",
  LABEL_WAITING_FOR_2: () => "to respond to request",
  LABEL_NO_ACTIVE_REQUESTS_INSTRUCTIONS: () =>
    "Looking for a request? Select the tabs above to review all active requests.",
  LABEL_NO_PAST_REQUESTS_INSTRUCTIONS: () =>
    "Looking for a request? Select the tabs above to review all past requests from the last 30 days.",
  LABEL_NO_RESULTS_HEADER: () => "No results",
  LABEL_NO_RESULTS_BODY: () => "Try adjusting or resetting your\n filters to see more requests.",
  LABEL_CREATE_A_REQUEST: () => "Create a request",
  LABEL_PENDING_SWING_REVIEW_TITLE: () => "Pending edits",
  LABEL_PENDING_SWING_REVIEW_MESSAGE: () =>
    "Changes to dates/times and any additional edits will be updated after Swing's review.",
  LABEL_PENDING_SWING_REVIEW: () =>
    "Our support team will be in touch regarding the dates/times of your request and any additional edits.",
  LABEL_DATES_TIMES: () => "Dates/Times",
  LABEL_EDITS_UNDER_REVIEW: () => "Edits under review",
  LABEL_ADDITIONAL_EDITS_ALERT: () =>
    "Dates/times and additional edits will be updated after Swing’s review.",
  LABEL_REQ_STAYS_OPEN: () => "Request stays open after start",
  LABEL_LATEST_ARRIVAL: () => "Latest arrival time:",
  LABEL_SUB_ARRIVAL: () => "Sub's expected arrival time is",

  /***** Components *****/
  /** RequestCard **/
  REQUEST_CARD_LABEL_DUPLICATE: () => "Duplicate",

  /** SubInfoCard **/
  SUB_INFO_CARD_TITLE: () => "Sub Info",
  SUB_INFO_CARD_CANCELLED_MESSAGE: () =>
    "This request was cancelled and subs will not be able to sign up.",
  SUB_INFO_CARD_UNFILLED_MESSAGE: () => "This request is still open to subs.",
  SUB_INFO_CARD_SEARCHING_MESSAGE: () => "We're searching for interested subs",
  SUB_INFO_CARD_FILLED_LABEL: () => "Filled by ",
  SUB_INFO_CARD_PENDING_MESSAGE_PRE: () => "Waiting for",
  SUB_INFO_CARD_PENDING_MESSAGE_POST: () => "to respond to request",
  SUB_INFO_CARD_TX_VERIFIER_BUTTON: () => "Reveal Full Name and DOB",
  SUB_INFO_CARD_COPIED: () => "Copied!",
  SUB_INFO_CARD_COPY_PHONE_LABEL: () => "Copy phone number",
  SUB_INFO_CARD_COPY_EMAIL_LABEL: () => "Copy email address",
  SUB_INFO_CARD_CLOSED_MESSAGE: () =>
    "The start time has passed and this request is no longer available to subs. If you still need coverage, adjust the start time or create a new request.",
  TX_VERIFIER_DESC: () =>
    "Use the above information to look up this sub's background check record in the TxDPS Fact Clearinghouse.",
  TX_VERIFIER_NAME: () => "Legal Name",
  TX_VERIFIER_DOB: () => "Birth Date",
  /** Chip **/
  CHIP_LABEL_FILLED: () => "Filled",
  CHIP_LABEL_FILLED_WITH_NAME: () => "Filled by ",
  CHIP_LABEL_UNFILLED: () => "Unfilled",
  CHIP_LABEL_COMPLETED: () => "Completed",
  CHIP_LABEL_COMPLETED_BY: () => "Completed by ",
  CHIP_LABEL_CANCELLED: () => "Cancelled",
  CHIP_LABEL_PENDING: () => "Waiting",
  CHIP_LABEL_WAITLIST: () => "Waiting",
  CHIP_LABEL_WAITLIST_MORE_THAN_ONE: ({ count }: { count: number }) => `Waitlist (${count})`,
  CHIP_LABEL_CLOSED: () => "Unfilled - Closed to subs",

  /** ActivityLog **/
  EVENT_LOG_LABEL_SUB_REQ_CREATED: (authorName: string) =>
    `Admin ${authorName} created this request`,
  EVENT_LOG_LABEL_SUB_REQ_CREATED_NOTIFY: () => `Swing is notifying subs`,
  EVENT_LOG_LABEL_EDIT_REQUEST: () => `This request has been edited`,
  EVENT_LOG_LABEL_SUB_REQ_CANCELED: (authorName: string) =>
    `Admin ${authorName} canceled this request`,
  EVENT_LOG_LABEL_SUB_LEFT_EARLY_APPROVED: () => `Sub left early with permission`,
  EVENT_LOG_LABEL_SUB_LEFT_EARLY_APPROVED_DELETED: () => `Sub left early with permission - Delete`,
  EVENT_LOG_LABEL_SUB_ADDED_TO_POOL: (subName: string) => `Sub ${subName} joined the sub pool`,
  EVENT_LOG_LABEL_SUB_ADDED_TO_POOL_ADMIN_CHOICE: (subName: string) =>
    `Sub ${subName} joined the waitlist`,
  EVENT_LOG_LABEL_SUB_CHOSEN: ({ authorName, subName }: { authorName: string; subName: string }) =>
    `Admin ${authorName} selected Sub ${subName} from the sub pool`,
  EVENT_LOG_LABEL_SUB_CHOSEN_ADMIN_CHOICE: ({
    authorName,
    subName,
  }: {
    authorName: string;
    subName: string;
  }) => `Admin ${authorName} selected Sub ${subName} from the waitlist`,
  EVENT_LOG_LABEL_SUB_CHOSEN_FIRST_RESPONSE: (subName: string) =>
    `Sub ${subName} accepted this request`,
  EVENT_LOG_LABEL_SUB_REMOVED_FROM_POOL: (subName: string) =>
    `Sub ${subName} has left the sub pool`,
  EVENT_LOG_LABEL_SUB_CANCELED: (subName: string) => `Sub ${subName} cancelled`,
  EVENT_LOG_LABEL_SUB_NOTIFIED: () => `Swing is notifying additional subs`,
  EVENT_LOG_LABEL_EDIT_FILLED_REQUEST: (authorName: string) =>
    `Admin ${authorName} edited this filled request`,
  EVENT_LOG_LABEL_FEEDBACK_ADDED: (authorName: string) => `Admin ${authorName} added feedback`,
  EVENT_LOG_LABEL_REQUEST_START_DATE_ADJUSTED: ({ startDate }: { startDate: DateTime }) => {
    const formattedDate = formatDate({
      date: startDate,
      format: "Wed, Jan 1 at 3:30 pm",
    });
    return `Start date has been adjusted${formattedDate ? ` to ${formattedDate}` : ""}.`;
  },
  EVENT_LOG_LABEL_URGENT_CONCERN: () => "On-campus concern logged",
  EVENT_LOG_LABEL_NO_SHOW_APPROVED: ({ date }: { date: DateTime }) =>
    `${formatDate({ date, format: "Wed, Jan 1" })} removed due to sub absence`,
  /** Report An Issue **/
  LABEL_NO_SHOW_CONFIRMED: () => "No-show (confirmed)",
  LABEL_NO_SHOW_UNCONFIRMED: () => "No Show Unconfirmed",
  LABEL_NO_SHOW: () => "Day removed due to sub absence",
  LABEL_LEFT_EARLY: () => "Sub Released Early",
  LABEL_ISSUES_LIST: () => "Reported Issues",
  LABEL_VIEW_ALL_ISSUES: () => "View all issues",
  REPORT_ISSUE_CONFIRM_HEADER: () => `Report Submitted`,
  REPORT_ISSUE_SECONDARY_BUTTON_TEXT: () => "Return to Request",
  REPORT_ISSUE_PRIMARY_BUTTON_TEXT: () => "Report Another Issue",
  REPORT_ISSUE_CONFIRM_DEFAULT_MESSAGE: () => "Your report has been submitted",
  // Old behavior (without feature flag)
  REPORT_ISSUE_CONFIRM_NO_SHOW_CONFIRMED_MESSAGE_OLD: () =>
    "We're sorry your sub didn't arrive! You will not be charged for this day and an email confirmation has been sent. If you still need coverage",
  REPORT_ISSUE_CONFIRM_NO_SHOW_CONFIRMED_MESSAGE_LINK_TEXT: () =>
    "please issue another sub request",
  REPORT_ISSUE_CONFIRM_NO_SHOW_UNCONFIRMED_MESSAGE_OLD: (subName: string) =>
    `Swing is attempting to contact ${subName}. We will follow up with you by phone and email within 30 minutes. Please edit this issue if either your sub arrives or confirms they will not be coming in.`,
  // New behavior (with feature flag)
  REPORT_ISSUE_CONFIRM_NO_SHOW_CONFIRMED_MESSAGE: () =>
    "We're sorry to hear your sub did not show up. We take no-shows seriously. No-shows affect sub's Swing Hero scores and ability to accept requests. You will not be charged for this day. We have recorded this as a no-show and will follow up with the sub. If you still need coverage, please create another sub request.",
  REPORT_ISSUE_CONFIRM_NO_SHOW_UNCONFIRMED_MESSAGE: () => `
  <p>We're sorry to hear your sub has not arrived. We take no-shows seriously. No-shows affect subs's Swing Hero scores and ability to accept requests.<p>
  <ul>
    <li>Our support team will investigate this report and try to locate the sub.</li>
    <li>We'll be in touch when we have an update.</li>
    <li>If we are unable to locate the sub, you will not be charged for the day.</li>
    </ul>
  </ul>`,
  REPORT_ISSUE_CONFIRM_NO_SHOW_APPROVED_MESSAGE: () => `
  <p>We're sorry to hear your sub was unavailable.<p>
  <ul>
    <li>You will not be charged for this day.</li>
    <li>We will follow up with the sub as needed.</li>
    <li>If you still need coverage, <a href="/#/request-a-sub/">please create another sub request</a>.</li>
    </ul>
  </ul>
  `,
  REPORT_ISSUE_CONFIRM_LEFT_EARLY_APPROVED_MESSAGE: () =>
    `Thanks for letting us know. Your sub will not be penalized for leaving early.`,
  REPORT_ISSUE_CONFIRM_LEFT_EARLY_UNAPPROVED_MESSAGE: () =>
    `Thanks for letting us know. We're sorry that your sub left without permission. We will follow up directly with the sub.`,
  REPORT_ISSUE_CONFIRM_IMMEDIATE_ASSISTANCE: () => `Call
  <a href="tel:1-650-413-9268,,2"> 1-650-413-9268</a> option 2 for immediate assistance`,
  REPORT_ISSUE_FORM_HEADER: () => `Report an Issue`,
  REPORT_ISSUE_FORM_SUBHEADER: (subName: string) => `Reporting an issue on sub: ${subName}`,
  REPORT_ISSUE_FORM_SELECT_DAY: () => `Select a day`,
  REPORT_ISSUE_FORM_SELECT_REASON: () => `Select reason`,
  REPORT_ISSUE_FORM_LATE: () => `Arrived late`,
  REPORT_ISSUE_FORM_LATE_LUNCH: () => `Late from lunch`,
  REPORT_ISSUE_FORM_LEFT_EARLY: () => `Left early`,
  REPORT_ISSUE_FORM_LEFT_EARLY_LABEL: () => `Did you release your sub early?`,
  REPORT_ISSUE_FORM_LEFT_EARLY_YES: () => `Yes, they had permission to leave early`,
  REPORT_ISSUE_FORM_LEFT_EARLY_NO: () => `No, they left without permission`,
  REPORT_ISSUE_FORM_NO_SHOW: () => `No-show`,
  REPORT_ISSUE_FORM_NO_SHOW_CONFIRM_LABEL: () => `Did the sub confirm that they can't come in?`,
  REPORT_ISSUE_FORM_NO_SHOW_ADVANCE_NOTICE_LABEL: () =>
    `Did your sub give advanced notice of their absence?`,
  REPORT_ISSUE_FORM_DETAILS_PHONE_LABEL: () =>
    `Leave a phone number for Swing to reach you (optional)`,
  REPORT_ISSUE_FORM_DETAILS_PHONE_PLACEHOLDER: () => `Phone number`,
  REPORT_ISSUE_FORM_DETAILS_LABEL: () => `Details`,
  REPORT_ISSUE_FORM_DETAILS_PLACEHOLDER: () => `e.g. Called sub, they did not pick up`,
  REPORT_ISSUE_FORM_URGENT_CONCERN: () => "Urgent on-campus concern",
  REPORT_ISSUE_ISSUES_HEADER: () => "Reliability Issues",
  REPORT_ISSUE_ISSUES_MESSAGE: (subName: string) => `Issues reported on ${subName}`,
  REPORT_ISSUE_LIST_PRIMARY_BUTTON_TEXT: () => "Report an Issue",
  REPORT_ISSUE_LIST_SECONDARY_BUTTON_TEXT: () => "Return to Request Details",

  /** Give Feedback **/
  GIVE_FEEDBACK_LABEL: () => "Review",
  GIVE_FEEDBACK_USE_PREVIOUS_REVIEW_LABEL: () => "Apply your school's previous review?",
  GIVE_FEEDBACK_PAST_REVIEW_SUMMARY_LABEL: ({ subName, date }: { subName: string; date: string }) =>
    `Your school gave ${subName} this review on ${date}.`,
  GIVE_FEEDBACK_REPEAT_REVIEW_SUBMISSION_ERROR_MSG: () =>
    "Review not submitted. Try again or <a href='mailto:support@swingeducation.com'>contact support</a>.",
  GIVE_FEEDBACK_PAST_REVIEW_CHANGE_LABEL: () => "Change",
  GIVE_FEEDBACK_PAST_REVIEW_KEEP_LABEL: () => "Keep",
  FEEDBACK_SUBMITTED_LABEL: () => "Review submitted",
  SCHOOL_FEEDBACK_LABEL: () => "School's Review",
  SCHOOL_FEEDBACK_SUBMITTED_BY: (adminName: string) => `Review submitted by ${adminName}`,
  SCHOOL_FEEDBACK_EXPERIENCE_LABEL: () => "Experience",
  SCHOOL_FEEDBACK_NOTES_LABEL: () => "Notes",
  SCHOOL_FEEDBACK_REVIEW_RATING_LABEL: () => "Overall experience:",
  SCHOOL_FEEDBACK_READ_MORE_LABEL: () => "Read More",
  SCHOOL_FEEDBACK_FAVORITED_SUB_LABEL: () => "Favorited Sub?",
  SCHOOL_FEEDBACK_BLOCKED_SUB_LABEL: () => "Blocked from school?",
  SCHOOL_FEEDBACK_BLOCKED_REASONS_LABEL: () => "Select additional reason(s)",
  SCHOOL_FEEDBACK_SAFETY_MESSAGING_TITLE: () => "Report your safety concern to Swing",
  SCHOOL_FEEDBACK_SAFETY_MESSAGING: () =>
    `<a target="_blank" rel="noreferrer" href="https://calendly.com/swingresponseandresolutions/30min">Schedule a call</a> with our Response and Resolutions Team to discuss the specifics of your safety concern.`,
  /** Give Feedback Blocked Reasons **/
  REASON_RELIABILITY: () => "Late or other reliability issue",
  REASON_DISENGAGED: () => "Disengaged with students",
  REASON_DIRECTIONS: () => "Failed to follow directions",
  REASON_MANAGEMENT: () => "Poor classroom management",
  REASON_ATTITUDE: () => "Unprofessional",
  REASON_SAFETY: () => "Safety",
  REASON_REPORTED_RELIABILITY_1: () => "Report issue(s)",
  REASON_REPORTED_RELIABILITY_2: () => "to select",
  /** Give Feedback Experience Labels **/
  POSITIVE_LABEL: () => "Positive",
  NEGATIVE_LABEL: () => "Negative",
  NEUTRAL_LABEL: () => "Neutral",
  /** Give Feedback Component Labels **/
  SCHOOL_FEEDBACK_EXPERIENCE_TITLE: () => "How was your experience?",

  /** Give Sub Feedback - V2 **/
  MSF_ADD_DETAILS_CTA: () => "Add Details",
  MSF_ADD_SUB_TO_FAVORITES: (subName?: string) =>
    `Add ${subName ?? "this sub"} to your school's favorites`,
  MSF_ADD_SUB_TO_FAVORITES_ALREADY_IN_FAVORITES: () =>
    "This sub is on your school's favorites list.",
  MSF_FEEDBACK_CONTENT_RATING_MESSAGE: (subName?: string) =>
    `Your feedback determines ${subName ?? "this sub"}'s ability to teach at your school in the future.`,
  MSF_FEEDBACK_QUESTION_INVITE_BACK: (subName?: string) =>
    `Review your experience with ${subName ?? "this sub"}:`,
  MSF_FEEDBACK_PUBLIC_SHARING_NOTE: () => "Only positive feedback will be shared publicly.",
  MSF_POSITIVE_FEEDBACK_COMMENT_LABEL: (subName?: string) =>
    `Let us know what ${subName ?? "this sub"} did well, and we'll add your comments to their public profile.`,
  MSF_COMMENT_TEXT_INPUT_PLACEHOLDER: () =>
    "Describe your school's experience with the sub in detail.",
  MSF_NEUTRAL_FEEDBACK_COMMENT_LABEL: (subName?: string) =>
    `Let us know more about your experience with ${subName ?? "this sub"}.`,
  MSF_NEUTRAL_FEEDBACK_COMMENT_PUBLIC_SHARING_NOTE: (subName?: string) =>
    `We won't share your response directly with ${subName ?? "this sub"}.`,
  MSF_NEUTRAL_SUMMARY_BODY: () =>
    "Your review helps us improve the Swing experience for both schools and subs",
  MSF_POSITIVE_FEEDBACK_REASON_ATTITUDE: () => "Professional",
  MSF_POSITIVE_FEEDBACK_REASON_DISENGAGED: () => "Engaged with students",
  MSF_POSITIVE_FEEDBACK_REASON_FLEXIBLE: () => "Flexible",
  MSF_POSITIVE_FEEDBACK_REASON_KNOWLEDGEABLE: () => "Knowledgeable on the subjects",
  MSF_POSITIVE_FEEDBACK_REASON_LABEL: () => "What went well?",
  MSF_POSITIVE_FEEDBACK_REASON_MANAGEMENT: () => "Great classroom management",
  MSF_POSITIVE_FEEDBACK_REASON_RELIABILITY: () => "Reliable",
  MSF_POSITIVE_SUMMARY_BODY: () =>
    "Your review helps us recognize our best subs and improve the Swing experience.",
  MSF_SUMMARY_TITLE: () => "Thank you for your review",
  MSF_REASON_OTHER: () => "Other",
  MSF_REASON_OTHER_TEXT_INPUT_LABEL: () => "Provide the reason",
  MSF_REASON_OTHER_TEXT_INPUT_PLACEHOLDER: () => "e.g. Great with office staff",
  MSF_NEGATIVE_NEUTRAL_FEEDBACK_COMMENT_PUBLIC_SHARING_NOTE: (subName?: string) =>
    `We won't share your response directly with ${subName ?? "this sub"}.`,
  MSF_NEGATIVE_NEUTRAL_FEEDBACK_COMMENT_LABEL: (subName?: string) =>
    `Let us know more about your experience with ${subName ?? "this sub"}.`,
  MSF_NEGATIVE_BLOCK_LABEL: () => "Do you want to block this sub?",
  MSF_NEGATIVE_BLOCK_SUB_LABEL: ({
    subName,
    schoolName,
  }: {
    subName?: string;
    schoolName: string;
  }) =>
    `If you block ${subName ?? "this sub"}, they will no longer be able to accept requests from ${schoolName}.`,
  MSF_NEGATIVE_BLOCK_FIRST_CHOICE: () => "Allow to return",
  MSF_NEGATIVE_BLOCK_SECOND_CHOICE: () => "Block from school",
  MSF_NEUTRAL_NEGATIVE_REASON_OTHER_TEXT_INPUT_PLACEHOLDER: () => "e.g. Behavior with office staff",
  MSF_NEUTRAL_FEEDBACK_REASON_ATTITUDE: () => "Be more professional",
  MSF_NEUTRAL_FEEDBACK_REASON_DISENGAGED: () => "Engage with students more",
  MSF_NEUTRAL_FEEDBACK_REASON_DIRECTIONS: () => "Follow instructions",
  MSF_NEUTRAL_FEEDBACK_REASON_MANAGEMENT: () => "Improve classroom management skills",
  MSF_NEUTRAL_FEEDBACK_REASON_PHONE: () => "Limit cell phone use",
  MSF_NEUTRAL_FEEDBACK_REASON_RELIABILITY: () => "Show up on time",
  MSF_NEUTRAL_FEEDBACK_REASON_LABEL: (subName?: string) =>
    `How can ${subName ?? "this sub"} improve?`,
  MSF_NEGATIVE_FEEDBACK_REASON_ATTITUDE: () => "Unprofessional",
  MSF_NEGATIVE_FEEDBACK_REASON_DISENGAGED: () => "Disengaged with students",
  MSF_NEGATIVE_FEEDBACK_REASON_DIRECTIONS: () => "Failed to follow instructions",
  MSF_NEGATIVE_FEEDBACK_REASON_MANAGEMENT: () => "Poor classroom management",
  MSF_NEGATIVE_FEEDBACK_REASON_PHONE: () => "Cell phone use",
  MSF_NEGATIVE_FEEDBACK_REASON_RELIABILITY: () => "Late or other reliability issues",
  MSF_NEGATIVE_FEEDBACK_REASON_SAFETY: () => "Safety",
  MSF_NEGATIVE_FEEDBACK_REASON_LABEL: () => "What didn't go well?",
  MSF_NEGATIVE_SUMMARY_BLOCKED: ({
    subName,
    schoolName,
  }: {
    subName?: string;
    schoolName: string;
  }) => `${subName ?? "This sub"} will no longer be able to accept requests for ${schoolName}.`,
  MSF_NEGATIVE_SUMMARY_SAFETY: () =>
    "We take reported safety events very seriously. Our Response and Resolutions team will investigate this incident and may follow up for more details.",
  MSF_NEGATIVE_SUMMARY_CONTACT: () =>
    "If you'd like to speak to someone immediately, contact us at ",
  MSF_NEGATIVE_SUMMARY_REVIEW: () =>
    "Our team will review this incident and may contact you for more details.",
  MSF_NEGATIVE_SUMMARY_DEFAULT: () =>
    "Our team will review your feedback and ensure that this sub has access to training.",
  MSF_CHOICE_BUTTON_POSITIVE_LABEL: () => "Good",
  MSF_CHOICE_BUTTON_NEUTRAL_LABEL: () => "Okay",
  MSF_CHOICE_BUTTON_NEGATIVE_LABEL: () => "Poor",

  /* Feedback already exists */
  FEEDBACK_EXISTS_TITLE: () => "This request has already been reviewed",
  FEEDBACK_EXISTS_BODY_1: ({ adminName, subName }: { adminName?: string; subName?: string }) =>
    adminName && subName
      ? `${adminName} left a review for ${subName}.`
      : "A review has already been created.",
  FEEDBACK_EXISTS_BODY_2: () =>
    "If you have questions or need to change your review please contact support.",

  /* Export Requests */
  EXPORT_REQUEST_LABEL: () => "Export to CSV",
  EXPORT_REQUEST_SUCCESS_LABEL: () => "Export complete",
  EXPORT_REQUEST_ERROR_LABEL: () => "Export Failed",
  EXPORT_REQUEST_SUCCESS_MSG: () =>
    "The file has been exported to CSV. Please check your downloads.",

  /* School Profile Pages */
  SAVE_CHANGES: () => "Save Changes",
  SCHOOL_WEBSITE_LABEL: () => "School Website",
  SCHOOL_WEBSITE_PLACEHOLDER: () => "Insert school website URL here.",
  SCHOOL_SUMMARY_LABEL: () => "School Summary",
  SCHOOL_SUMMARY_PLACEHOLDER: () => "Describe school summary here.",

  SCHOOL_DETAILS_HEADER: () => "School Details",
  SCHOOL_SCHEDULE_LABEL: () => "School Schedule",

  BILLING_DETAILS_HEADER: () => "Billing Details",
  INVOICE_TYPE_LABEL: () => "Invoice Type",
  PRIMARY_BILLING_EMAIL_LABEL: () => "Primary Billing Email",
  RATES_LABEL: () => "Rates",
  HALF_DAY_LABEL: () => "Half Day (<=4 hrs)",
  FULL_DAY_LABEL: () => "Full Day (>4 hrs)",
  CREDENTIAL_TYPES_LABEL: () => "Credential Types",
  SHORT_TERM_SUFFIX: () => "Short-Term Rate",
  LONG_TERM_SUFFIX: () => "Long-Term Rate",

  /*** SchoolYears ***/
  SCHOOL_YEAR_HEADER: () => "School Year",
  SCHOOL_YEAR_DESCRIPTION: () => "Enter your school year information.",
  SCHOOL_YEAR_START_LABEL: () => "First day",
  SCHOOL_YEAR_END_LABEL: () => "Last day",
  SCHOOL_YEAR_EDIT_ARIA_LABEL: () => "Edit School Year",
  SCHOOL_YEAR_INPUT_DISABLED_LABEL: () => "dates outside school year",
  SCHOOL_YEAR_START_AFTER_END_DISABLED_LABEL: ({ endDate }: { endDate: DateTime }) =>
    `days after the last day of the school year (${formatDate({
      date: endDate,
      format: "Jan 1, 2025",
    })})`,
  SCHOOL_YEAR_END_BEFORE_START_DISABLED_LABEL: ({ startDate }: { startDate: DateTime }) =>
    `days before the first day of school year (${formatDate({
      date: startDate,
      format: "Jan 1, 2025",
    })})`,

  /*** SchoolClosures ***/
  SCHOOL_CLOSURES_LABEL: () => "Label",
  SCHOOL_CLOSURES_DAYS_HEADER: () => "Non-School Days",
  SCHOOL_CLOSURES_DAYS_DESCRIPTION: () =>
    "Add your non-school days. We'll exclude these days from all your requests.",
  SCHOOL_CLOSURES_ADD_DAYS_LABEL: () => "Add Day(s)",
  SCHOOL_CLOSURES_REASON_PLACEHOLDER: () => "e.g. Spring Break",
  ARIA_SCHOOL_CLOSURES_REMOVE_DAYS: () => "Remove Day(s)",
  ARIA_SCHOOL_CLOSURES_RESTORE_DAYS: () => "Restore Day(s)",
  SCHOOL_CLOSURES_NON_SCHOOL_DAYS_OTHER_DISABLED_LABEL: () => "other non-school days",
  SCHOOL_CLOSURES_NON_SCHOOL_DAYS_OVERLAPPING_DISABLED_LABEL: () => "overlapping non-school days",
  SCHOOL_CLOSURES_NON_SCHOOL_DAYS_START_DISABLED_LABEL: ({ endDate }: { endDate: DateTime }) =>
    `days past the current end date for this entry (${formatDate({
      date: endDate,
      format: "Jan 1, 2025",
    })})`,
  SCHOOL_CLOSURES_NON_SCHOOL_DAYS_END_DISABLED_LABEL: ({ startDate }: { startDate: DateTime }) =>
    `days before the current start date for this entry (${formatDate({
      date: startDate,
      format: "Jan 1, 2025",
    })})`,
  SCHOOL_CLOSURES_EMPTY_LABEL: () =>
    "You have no non-school-days for this school year. Please click the edit pencil to add new non-school-days.",

  ARRIVING_ONSITE_LABEL: () => "Arriving Onsite",
  DAY_OF_CONTACT_HEADER: () => "Day Of Contact",
  DAY_OF_CONTACT_NAME_LABEL: () => "Day-Of Contact Name",
  DAY_OF_CONTACT_NAME_PLACEHOLDER: () => "Carmen",
  DAY_OF_CONTACT_PHONE_LABEL: () => "Phone",
  DAY_OF_CONTACT_PHONE_PLACEHOLDER: () => "555-555-5555",
  DAY_OF_CONTACT_EMAIL_LABEL: () => "Email",
  DAY_OF_CONTACT_EMAIL_PLACEHOLDER: () => "carmen@email.com",
  PARKING_HEADER: () => "Parking",
  PARKING_COST_LABEL: () => "Parking Cost (Hourly)",
  PARKING_COST_PLACEHOLDER: () => "$2.00",
  PARKING_LOCATION_LABEL: () => "Parking Location",
  PARKING_LOCATION_PLACEHOLDER: () => "Faculty parking behind the main office",
  LOCATION_HEADER: () => "Location",
  CHECK_IN_PROCEDURE_HEADER: () => "Check-in Procedure",
  CHECK_IN_LOCATION_LABEL: () => "Check-in Location",
  CHECK_IN_LOCATION_PLACEHOLDER: () => "Front Office",
  CHECK_IN_PROCEDURE_LABEL: () => "Check-in Procedure",
  CHECK_IN_PROCEDURE_PLACEHOLDER: () => "Sign in with front desk",
  CHECK_OUT_PROCEDURE_HEADER: () => "Check-out Procedure",
  CHECK_OUT_LOCATION_LABEL: () => "Check-out Location",
  CHECK_OUT_LOCATION_PLACEHOLDER: () => "Front Office",
  CHECK_OUT_PROCEDURE_LABEL: () => "Check-out Procedure",
  CHECK_OUT_PROCEDURE_PLACEHOLDER: () => "Drop off keys and sign out",

  SITE_POLICIES_HEADER: () => "Site Policies",
  TAKING_ATTENDANCE_LABEL: () => "Taking Attendance",
  TAKING_ATTENDANCE_PLACEHOLDER: () => "Digital check-in",
  ESCALATION_LABEL: () => "Escalation Procedure",
  ESCALATION_PLACEHOLDER: () => "Call front office",
  SUBSTITUTES_HEADER: () => "Substitutes",
  STUDENTS_HEADER: () => "Students",
  LEAVING_CLASSROOM_LABEL: () => "Leaving the Classroom",
  SUBS_LEAVING_CLASSROOM_PLACEHOLDER: () => "Do not leave students unattended",
  STUDENTS_LEAVING_CLASSROOM_PLACEHOLDER: () => "Must wait for teacher dismissal",
  LUNCH_PROTOCOL_LABEL: () => "Lunch Protocol",
  SUBS_LUNCH_PROTOCOL_PLACEHOLDER: () => "Subs may not leave campus, please use teacher's lounge",
  STUDENTS_LUNCH_PROTOCOL_PLACEHOLDER: () => "Escort students to cafe",
  DRESS_CODE_LABEL: () => "Dress Code",
  SUBS_DRESS_CODE_PLACEHOLDER: () => "Business casual",
  STUDENTS_DRESS_CODE_PLACEHOLDER: () => "School uniform",
  ELECTRONICS_LABEL: () => "Electronics",
  SUBS_ELECTRONICS_PLACEHOLDER: () => "No phone use during class time",
  STUDENTS_ELECTRONICS_PLACEHOLDER: () => "May use phones on break",
  EMERGENCY_PROCEDURES_HEADER: () => "Emergency Procedures",
  PROTOCOL_HEADER: () => "Protocol",
  PROTOCOL_PLACEHOLDER: () => "Call front office",
  CODE_WORDS_HEADER: () => "Code Words",
  CODE_WORDS_HEADER_PLACEHOLDER: () => "Apple",
  LOCKDOWN_HEADER: () => "Lockdown Expectations/Consequences",
  LOCKDOWN_PLACEHOLDER: () => "See Teacher handbook",

  /** Request Details Page **/
  REQUEST_ALERT_CREATED_HEADER: () => "Your request has been created",
  REQUEST_ALERT_CREATED_MESSAGE: () => "Notifying high-quality subs in your area.",
  REQUEST_ALERT_START_DATE_AUTO_ADJUSTED_TITLE: () => "Auto-adjusted to find a sub",
  REQUEST_ALERT_START_DATE_AUTO_ADJUSTED: () =>
    "We auto-adjusted your start date since your request wasn't filled yet. We'll keep adjusting until it is filled or shortens enough to remove the waitlist, then we'll cancel. Please cancel if you no longer need a sub.",

  /** Side Menu **/
  SIDE_MENU_LABEL_ACTIVE_REQUESTS: () => "Active Requests",
  SIDE_MENU_LABEL_PAST_REQUESTS: () => "Past Requests",
  SIDE_MENU_LABEL_REQUEST_A_SUB: () => "Request a Sub",
  SIDE_MENU_LABEL_SCHOOLS: () => "School Profiles",
  SIDE_MENU_LABEL_BILLING: () => "Billing Portal",

  /** More Section */
  CHAT_WITH_SUPPORT: () => "Get assistance",
  HELP_CENTER: () => "Help Center",
  SWING_SUPPORT: () => "Swing Support",
  SIGN_OUT: () => "Sign Out",

  /** Routes Table **/
  // We don't make a distinction between desktop and mobile titles for now
  ROUTE_TABLE_ACTIVE_REQUESTS: () => "Active Requests",
  ROUTE_TABLE_PAST_REQUESTS: () => "Past Requests",
  ROUTE_TABLE_REQUEST_DETAILS: () => "Request Details",
  ROUTE_TABLE_SUB_PROFILE: () => "Sub Profile",
  ROUTE_TABLE_REQUEST_SUB: () => "Request a Sub",
  ROUTE_TABLE_BILLING_PAGE: () => "Billing Portal",
  ROUTE_TABLE_VIEW_BILLING_PORTAL: () => "View Billing Portal",
  ROUTE_TABLE_VIEW_BILLING_PORTAL_DISABLED: () => "View Billing Portal: Log in on web to view",
  ROUTE_TABLE_MORE: () => "More",
  ROUTE_TABLE_SCHOOLS: () => "Schools",
  ROUTE_TABLE_SCHOOL: () => "School Details",

  /** Aria labels **/
  ARIA_LABEL_SELECTED: () => "selected",
  ARIA_LABEL_DESELECTED: () => "deselected",

  /** SubLocator Tool **/
  LABEL_END_OF_PAGE: () => "You've reached the end",
  LOCATION_SEARCH_CONTACT_SUPPORT_1: () => "Contact support",
  LOCATION_SEARCH_INVALID_ZIP_CODE: () => "Invalid zipcode",
  SWING_SUPPORT_LINK_GENERAL_REQUEST: () => "/hc/en-us/requests/new",
  SUB_MAP_EXPERIENCE_DAYS_WORKED: () => "days worked",
  SUB_MAP_EXPERIENCE_POSITIVE_REVIEWS: () => "positive reviews",
  SUB_MAP_EXPERIENCE_SCHOOLS_WORKED: () => "schools worked",
  SUB_MAP_EXPERIENCE_TIMES_PREFERRED: () => "times preferred",
  SUB_MAP_SWING_EXPERIENCE: () => "Swing Experience",

  /** Select sub **/
  SUB_PROFILE_LABEL: () => "Sub Profile",
  SELECT_THIS_SUB_LABEL: () => "Select this sub",

  /** Sub map **/
  SUB_LOCATOR: () => "Sub Locator",
  SUPERADMIN: () => "Superadmin",
  LOADING: () => "Loading...",
  SUB_MAP_SUBS_NOT_FOUND: () => "No subs found with these criteria.",
  SUB_MAP_SEARCHING_BY: () => "Searching by:",
  SUB_MAP_MATCH_ALL: () => "Showing subs that match ALL the following criteria:",

  /** Billing Page **/
  BILLING_PAGE_BACK_BUTTON: () => `More`,
  BILLING_PAGE_PREVIOUS_YEAR: ({
    startDate,
    endDate,
  }: {
    startDate: DateTime;
    endDate: DateTime;
  }) => `${startDate.year} to ${endDate.year} school year`,
  BILLING_PAGE_CURRENT_YEAR: ({ startDate, endDate }: { startDate: DateTime; endDate: DateTime }) =>
    `Current: ${string_table.BILLING_PAGE_PREVIOUS_YEAR({ startDate, endDate })}`,
  BILLING_PAGE_DATE_RANGE: ({ startDate, endDate }: { startDate: DateTime; endDate: DateTime }) =>
    `${startDate.toFormat("LL/dd/yyyy")} - ${endDate.toFormat("LL/dd/yyyy")}`,
  BILLING_PAGE_CONTACT_MESSAGE: () =>
    `Please reach out to <a href="mailto:accounting@swingeducation.com">accounting@swingeducation.com</a> to access copies of invoices.`,
  BILLING_PAGE_TOAST_MESSAGE: ({ email }: { email: string }) =>
    `Billing report sent to ${email}. It should arrive within a few minutes.`,

  /** Billing Portal **/
  BILLING_PORTAL_TAB_INSIGHTS: () => "Insights",
  BILLING_PORTAL_TAB_INVOICES: () => "Invoices",
  BILLING_PORTAL_TAB_BUDGETS: () => "Budget",
  BUDGETS_EMAIL_DISCLAIMER: () =>
    "We'll notify the billing email when utilization reaches 75%, 90% and 100%.",
  BUDGETS_OVER_LIMIT: () => "over budget",
  BUDGETS_LABEL: () => "Budgets",
  BUDGETS_SITE_LABEL: () => "Site",
  BUDGETS_ANNUAL_LIMIT_LABEL: () => "Annual Limit",
  BUDGETS_ANNUAL_LIMIT_SUB: ({ years }: { years: string }) => `${years} SY`,
  BUDGETS_STATUS_LABEL: () => "Status",
  BUDGETS_EDIT_TOAST: ({ budgetName }: { budgetName: string }) =>
    `Budget ${budgetName} edited successfully.`,
  BUDGETS_SEARCH_PLACEHOLDER: () => "Search for a budget",
  BUDGETS_EMPTY_STATE: () =>
    "You don't have multi-budget billing enabled. Contact support to set it up.",
  BUDGETS_ACTIVE: () => "Active",
  BUDGETS_INACTIVE: () => "Inactive",
  BUDGETS_NOT_ENOUGH_DATA: () => "N/A",
  BUDGETS_NO_LIMIT: () => "Add budget limit to view.",
  BUDGETS_SPEND_LABEL: () => "Spend",
  BUDGETS_UTILIZATION_LABEL: () => "Utilization",
  BUDGETS_SY_LABEL: () => "SY to date",
  BUDGETS_PCT_LABEL: () => "% of limit spent",
  BUDGETS_RESET_LABEL: () => "Annual limits, spend, and utilization reset each year on July 1st.",

  /** Insights **/

  INSIGHTS_SY_SUFFIX: () => "School Year",
  INSIGHTS_TOTAL_SPEND: () => "Total Spend",
  INSIGHTS_DAYS_COVERED: () => "Days covered",
  INSIGHTS_DAYS: () => "Days",
  INSIGHTS_COVERED: () => "Covered",
  INSIGHTS_FILL_RATE_HEADER: () => "Fill Rate",
  INSIGHTS_OVERALL: () => "Overall",
  INSIGHTS_SPED: () => "Special Education",
  INSIGHTS_LM: () => "Last-minute (<12 hours)",
  INSIGHTS_SITE_BREAKDOWN_HEADER: () => "Site Breakdown",
  INSIGHTS_NA: () => "n/a",
  INSIGHTS_SITE: () => "Site",
  INSIGHTS_SPEND: () => "Spend",
  INSIGHTS_SY_TO_DATE: () => "SY to date",
  INSIGHTS_SPED_SHORT: () => "Special ed",
  INSIGHTS_LM_SHORT: () => "Last-minute",
  INSIGHTS_FR: () => "Fill rate",

  /** Sub Profile **/
  SUB_PROFILE_ABOUT_HEADER: () => "About Me",
  SUB_PROFILE_CONTACT_IMAGE_ALT: () => "generic avatar user icon",
  SUB_PROFILE_CONTACT_EDIT_BUTTON: () => "Edit",
  SUB_PROFILE_EDUCATION_HEADER: () => "Education",
  SUB_PROFILE_FEEDBACK_FROM_SCHOOLS_HEADER: () => "Feedback From Schools",
  SUB_PROFILE_QUALIFICATIONS_HEADER: () => "Qualifications",
  SUB_PROFILE_NO_INFORMATION: () => "No information provided",
  SUB_PROFILE_RELEVANT_WORK_EXPERIENCE_BUTTON_TEXT: () => "Add Another Experience",
  SUB_PROFILE_RELEVANT_WORK_EXPERIENCE_CONTENT: () => "Showcase your work experience here!",
  SUB_PROFILE_RELEVANT_WORK_EXPERIENCE_HEADER: () => "Relevant Experience",
  SUB_PROFILE_SWING_EXPERIENCE_DAYS_WORKED: () => "Days Worked",
  SUB_PROFILE_SWING_EXPERIENCE_HEADER: () => "Swing Experience",
  SUB_PROFILE_SWING_EXPERIENCE_POSITIVE_REVIEWS: () => "Positive Reviews",
  SUB_PROFILE_SWING_EXPERIENCE_SCHOOLS_WORKED: () => "Schools Worked",
  SUB_PROFILE_SWING_EXPERIENCE_TIMES_PREFERRED: () => "Times Preferred",

  /** Swing Heroes **/
  SWING_HERO: () => "Swing Hero",
  SWING_HEROES: () => "Swing Heroes",
  SWING_HERO_SUPPORT_LINK: () =>
    "/hc/en-us/articles/34892888609171-Swing-Heroes-for-Swing-School-Admins",
  SWING_HERO_DESCRIPTION: () => "The top-performing, most reliable educators.",
  SWING_HERO_LEARN_MORE: () => "Learn more about",

  /** Request Form **/
  // TODO: Deprecate once LTR V2 is fully launched
  // https://linear.app/swing-education/issue/MKTP-685/[fe]-remove-unused-code
  REQUEST_FORM_LONG_TERM_DISCLAIMER: ({ schoolName }: { schoolName: string }) =>
    `Your request is longer than 3 weeks. If you would like to change or delete specific dates and times for this long-term request, enter them below to ensure correct billing. (We have assumed default hours for ${schoolName}). If you require no changes or deletes, write "N/A"`,
  REQUEST_FORM_SELECT_DATE_DAY_COUNT: ({ count }: { count: number }) =>
    count === 1 ? "1 Day Selected" : `${count} Days Selected`,
  REQUEST_FORM_SELECT_DATE_TIMEZONE: ({ timezone }: { timezone: string }) =>
    `Times are displayed in ${timezone}.`,
  REQUEST_FORM_SELECT_DATE_SCHOOL_CLOSURE_1: () =>
    `Manage your school's default hours and non-school days in your `,
  REQUEST_FORM_SELECT_DATE_SCHOOL_CLOSURE_2: () => `school profile`,
  REQUEST_FORM_SELECT_DATE_MODAL_HEADER: () => `All dates and times`,
  REQUEST_FORM_SELECT_DATE_MODAL_SAVE_LABEL: ({ days }: { days: number }) => `Save ${days} Days`,
  REQUEST_FORM_SELECT_DATE_SEE_ALL_DAYS: () => `See all dates and times`,
  REQUEST_FORM_SELECT_DATE_ADDITIONAL_EDITS_LABEL: () => `Any additional edits you'd like to make?`,
  REQUEST_FORM_SELECT_DATE_ADDITIONAL_EDITS_PLACEHOLDER: () => "e.g. Remove every other Thursday",
  REQUEST_FORM_SELECT_DATE_ADDITIONAL_EDITS_TOGGLE_LABEL: () =>
    `I need support editing times for many days`,
  REQUEST_FORM_SELECT_DATE_ADDITIONAL_EDITS_INPUT_LABEL: () =>
    `What additional edits would you like to make?`,
  REQUEST_FORM_QUESTION_CREDENTIAL_LABEL: () => `Credential/Permit Required?`,
  REQUEST_FORM_QUESTION_LESSON_PLAN_LABEL: () => `Lesson Plan Provided?`,
  WAITLIST_AUTO_FILL_HEADER: () => `Waitlist auto-fill`,
  WAITLIST_AUTO_FILL_LABEL: () =>
    `We’ll automatically choose a great sub for you. No need to select one off the waitlist.`,

  /*** MultiDateField ***/
  MULTI_DATE_FIELD_DISABLED_SCHOOL_CLOSURE_LABEL: () => "non-school days",
  MULTI_DATE_FIELD_DISABLED_START_LABEL: () => "dates before today",
  MULTI_DATE_FIELD_DISABLED_END_LABEL: ({ startDate }: { startDate: DateTime }) =>
    `dates before Start Date (${formatDate({
      date: startDate,
      format: "Jan 1, 2025",
    })})`,

  /***** Budget Field *****/
  LABEL_SCHOOL_BUDGETS: () => "School Budgets",
  LABEL_DISTRICT_BUDGETS: () => "District Budgets",

  /***** Notes To Sub *****/
  NOTES_TO_SUB_SPED_ALERT_TITLE: () => "Help your sub prepare by including:",
  NOTES_TO_SUB_SPED_ALERT_LIST: () => `
    • Presence of support staff <br />
    • Class setup (1:1, self-contained) <br />
    • Level of specialized instruction <br />
    • Other relevant info (disability types, documentation, etc) <br />
  `,
  NOTES_TO_SUB_LABEL: () => "Notes to Sub",
  NOTES_TO_SUB_REQ_MEMO_ERROR: () => "Please enter your Notes to Sub",
  NOTES_TO_SUB_PLACEHOLDER: () => "Please include details about the classroom arrangement",

  /** Sub Schedule Conflicting Days **/
  DAYS_CONFLICT_HEADER: () => "Sub unavailable some days",
  DAYS_CONFLICT_DESCRIPTION: () => "These days are excluded from your request:",
  LABEL_SHOW_ALL_DAYS: () => "Show all days",
  LABEL_SUB_UNAVAILABLE_DAYS: () => "days the sub may be unavailable",

  /** Request Index Filter Modal **/

  FILTERS_HEADER: () => "Filters",
  FILTERS_MODAL_APPLY_BUTTON: () => "Apply",
  FILTERS_MODAL_CLEAR_BUTTON: () => "Clear",
  FILTER_MODAL_SHOW_REQUESTS_LABEL: () => "Show requests within this timeframe",
  FILTER_MODAL_CUSTOM_DATE: () => "Custom Date Range",
  FILTER_MODAL_DATE_DISCLAIMER: () => "Export CSV to view requests older than 30 days.",

  /** Bulk Edit Times Modal **/

  BULK_EDIT_TIMES_ENTRY: () => "Bulk edit times",
  EDIT_TIMES_MODAL_HEADER: () => "Edit times",
  EDIT_TIMES_ALL_DAYS: () => "All days",
  EDIT_TIMES_MONDAYS: () => "Mondays",
  EDIT_TIMES_TUESDAYS: () => "Tuesdays",
  EDIT_TIMES_WEDNESDAYS: () => "Wednesdays",
  EDIT_TIMES_THURSDAYS: () => "Thursdays",
  EDIT_TIMES_FRIDAYS: () => "Fridays",

  /** Review Request Submission Modal **/
  REQUEST_REVIEW_TITLE: () => "Review Request",
  REQUEST_REVIEW_STARTS_SOON: () =>
    "Request starts soon. We will share sub ETA via email when they sign up.",
  REQUEST_REVIEW_KEEP_OPEN: () =>
    "If unfilled at the start time, would you like to keep your request open?",
  REQUEST_REVIEW_LATEST_Q: () => "What's the latest time a sub can arrive?",
  REQUEST_REVIEW_ERROR_MESSAGE: () =>
    "Enter a time after the request starts. The request must be at least 1 hour long.",

  EXTREME_TIMES_1: () => "Correct time?",
  EXTREME_TIMES_2: () => "Outside typical times or length",

  /** Sped Details **/

  SPED_SUPPORT_LEVEL: () => "Support Level",
  SPED_SUPPORT_LEVEL_MILD: () => "Mild/Moderate",
  SPED_SUPPORT_LEVEL_SEVERE: () => "Moderate/Severe (ESN: Extensive Support Needs)",
  SPED_SETTING: () => "Setting",
  SPED_SETTING_TEACHING_HEADER: () => "Co-teaching",
  SPED_SETTING_TEACHING_LABEL: () => "Team-teach with gen ed teacher",
  SPED_SETTING_PUSH_IN_HEADER: () => "Push-in",
  SPED_SETTING_PUSH_IN_LABEL: () => "Direct support in the classrom",
  SPED_SETTING_PULL_OUT_HEADER: () => "Pull-out",
  SPED_SETTING_PULL_OUT_LABEL: () => "Separate intensive instruction",
  SPED_SETTING_SDC_HEADER: () => "Self-contained/SDC",
  SPED_SETTING_SDC_LABEL: () => "Special ed classroom only",
  SPED_TOILETING_REQUIRED: () => "Toileting/Diapering Required?",

  LABEL_SPED: () => "Special Education",
  LABEL_TOILETING_REQUIRED: () => "Toileting/Diapering Required",
  LABEL_SPED_SETTING_CO_TEACHING: () => "Co-Teaching",
  LABEL_SPED_SETTING_PUSH_IN: () => "Push-in",
  LABEL_SPED_SETTING_PULL_OUT: () => "Pull-out",
  LABEL_SPED_SETTING_SDC: () => "Self-contained/SDC",

  LABEL_SPED_SUPPORT_MILD: () => "Mild/Moderate",
  LABEL_SPED_SUPPORT_SEVERE: () => "Moderate/Severe (ESN: Extensive Support Needs)",
};

/**
 * Given a translation key, optionally with arguments, returns the translated
 * string.
 */
export function translate<Key extends keyof typeof string_table>(
  key: Key,
  ...args: Parameters<(typeof string_table)[typeof key]>
) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return string_table[key](args[0] as any);
}
